<template>
  <div>
    <div>
      <md-radio
        v-for="model in categorySelectModel"
        :value="model.value"
        :model="categorySelect"
        @change="$emit('update:categorySelect', $event)"
        :key="model.value"
        >{{ model.name }}</md-radio
      >
    </div>
    <div :class="{'disable-input': categorySelect == 'all' }">
      <md-radio
        v-for="level in itemCategoryLevels"
        :value="level.level"
        :model="categoryLevel"
        @change="$emit('update:categoryLevel', $event)"
        :key="level.level"
        >{{ level.name }}</md-radio
      >
      <gl-tree-view
        :entries="itemCategories"
        :selected="categories"
        @update:selected="$emit('update:categories', $event)"
      />
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import GlTreeView from '@/components/common/GlTreeView.vue'

/**
 * 商品分類階層レベルを階層インデックス（何階層目か、0始まり）を取得する
 */
function toCateLevelToTreeLevel(itemCategoryLevels, targetLevel) {
  return itemCategoryLevels.findIndex(l => l.level === targetLevel)
}

/**
 * ノードの再帰処理
 *
 * 上から処理対象とする。
 */
function walk(entry, level, f) {
  f(entry, level)
  if (entry.children) {
    for (const child of entry.children) {
      walk(child, level + 1, f)
    }
  }
}

/**
 * 「商品モード - 分類選択時」コンポーネント
 */
export default {
  components: { GlTreeView },
  mixins: [],
  props: {
    /**
     * 分類をロードする対象の企業コード
     */
    company: {
      type: String,
    },
    /**
     * 全分類、分類選択
     */
    categorySelect: {
      type: String,
    },
    /**
     * 選択している分類階層
     */
    categoryLevel: {
      type: Number,
    },
    /**
     * 選択されているカテゴリ（表示されている最下層）
     */
    categories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      categorySelectModel: [
        {
          value: 'all',
          name: '全分類',
        },
        {
          value: 'select',
          name: '分類指定',
        },
      ],
      /**
       * 分類階層レベルマスタ
       */
      itemCategoryLevels: [],
      /**
       * 分類の階層構造データ
       */
      itemCategoriesCache: [],
      /**
       * ツリー用の分類の階層構造データ
       *
       * 選択されている階層に応じて階層構造データが変わる（選択されている階層
       * 以下の階層は表示しない）ので、分類データ全体のキャッシュとは別に分類
       * データを保持する
       * TODO 大きくなる傾向のデータなので、なるべくメモリに持ちたくない、、、
       */
      itemCategories: [],

      oldCompany: undefined,
    }
  },
  watch: {
    /**
     * 企業が決まることで階層一覧と分類一覧が取得できる
     */
    company: {
      immediate: true,
      async handler(company) {
        if (!company) {
          return
        }

        // this.itemCategoryLevels = this.$store.state.itemCategoryLevels[company]
        this.itemCategoryLevels = await this.$itemCategoryLevels.get(company)
        this.itemCategoriesCache = await this.$itemCategories.get(company)

        if (!this.itemCategoryLevels) {
          return
        }

        if (!this.categoryLevel) {
          // 必ずどこかの階層選択は必要なので、指定されていない場合は最下層とする
          this.$emit(
            'update:categoryLevel',
            this.itemCategoryLevels[this.itemCategoryLevels.length - 1].level,
          )
        }
        this.oldCompany = company
        this.setEntries()
      },
    },
    categoryLevel(newVal, oldVal) {
      // 階層を変えた場合はもともと選択されていた要素は保持しないので、
      // 親コンポーネントに対して選択をクリアする旨を通知する必要がある。
      // ただし、コンポーネントロード直後（初回）変更時には親からもらった
      // データで描画するので何もする必要はない。

      //企業が同じ場合のみ階層変更でクリア処理を行う
      //企業を変更したときでも、階層変更がトリガーになって
      //動いてクリアされてしまうため
      if (oldVal !== undefined && this.company === this.oldCompany) {
        this.$emit('update:categories', [])
        this.setEntries()
      }
      this.oldCompany = this.company
    },
    categorySelect(value) {
      //全分類を選択した場合はクリア
      if (value === 'all') {
        this.$emit('update:categories', [])
        this.setEntries()
      }
    },
  },
  methods: {
    setEntries() {
      const levelUntil = toCateLevelToTreeLevel(
        this.itemCategoryLevels,
        this.categoryLevel,
      )

      // TODO 一回ツリー構造を全部Cloneしてから指定分類配下を削除している
      //      パフォーマンスの問題が出たらいい方法を考える。
      const filteredByLevel = cloneDeep(this.itemCategoriesCache)

      if (levelUntil < this.itemCategoryLevels.length - 1) {
        for (const top of filteredByLevel) {
          walk(top, 0, (entry, level) => {
            if (level === levelUntil) {
              entry.children = undefined
            }
          })
        }
      }

      if (this.categories && this.categories.length > 0) {
        const categoriesSet = new Set(this.categories)

        for (const topLevelElement of filteredByLevel) {
          walk(topLevelElement, 0, entry => {
            if (entry.children) {
              return
            }
            entry.state = categoriesSet.has(entry.code) ? 'checked' : 'none'
          })
        }
      }

      this.itemCategories = [
        {
          code: undefined,
          name: '全分類',
          children: filteredByLevel,
          open: true,
        },
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  overflow: hidden;
}

.line {
  margin-top: 32px;
  &:first-child {
    margin-top: 0;
  }
}

.tree-view-container {
  height: 350px;
  overflow: auto;
  border: 1px solid var(--color-border);
  border-radius: 5px;
  padding: var(--spacing-medium);
}

.disable-input {
  background-color: #c0c0c0;
  opacity: 0.5;
  pointer-events: none;
}
</style>
